/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { Tab, Tabs } from 'react-bootstrap';
import { Grid3x3GapFill, Search } from 'react-bootstrap-icons';
import Spinner from 'react-bootstrap/Spinner';
import AllGames from 'src/conponents/Home/AllGames';
import { DEFAULT_CONFIGS } from 'src/constants';
import useSearchQueryParams from 'src/hooks/useSearchQueryParams';
import './index.scss';
import useTranslate from 'src/hooks/useTranslate';
import { useLanguageContext } from 'src/context/languageContext';
import useHistory from 'src/hooks/useHistory';
import useSort from 'src/hooks/useSort';
import { useNumber } from 'src/hooks/number';
import useTheme from 'src/hooks/useTheme';
import { usePreventTranslate } from 'src/hooks/usePreventTranslate';
import PropTypes from 'prop-types';
import { isPxplay, isRc } from 'src/conponents/Shared/Header';
import GameSort from './GameSort';

const otherId = 'others';
const allProviders = {};
const providerMetas = {
  rc: {
    index: 0,
  },
  ca: {
    index: 1,
  },
  pg: {
    index: 2,
    popularIndex: 100000,
  },
  pp: {
    index: 3,
    popularIndex: 100000,
  },
  hs: {
    index: 4,
    popularIndex: 0,
  },
  rg: {
    index: 5,
  },
  as: {
    index: 6,
  },
  others: {
    id: otherId,
    name: 'Others',
    code: otherId,
    index: 9999,
  },
};
const gameTypeMetas = {
  SLOT: {
    index: 0,
  },
  CASUAL: {
    index: 1,
  },
};
const topRcCasualGames = [
  'prosperity-clash',
  'crash',
  'balonismo',
  'chicken-uncrossable',
  'mines',
  'pig-mines',
  'dragon-crash',
];

function HomeContainer() {
  const { i18n } = useLanguageContext();
  const { updateSearchParams } = useHistory();
  const [searchTerm, setSearchTerm] = useState('');
  const [originalData, setOriginalData] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const { sortBy, sortDirection } = useSort();
  const { round } = useNumber();
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';

  const oc = useSearchQueryParams('oc', DEFAULT_CONFIGS.brandCode);
  const t = useSearchQueryParams('t', DEFAULT_CONFIGS.playerToken);
  const l = useSearchQueryParams('l', DEFAULT_CONFIGS.language);
  const values = useSearchQueryParams();

  const [filterBy, setFilterBy] = useState(values.get('filter_by') || 'all');
  const handleSearchGame = (e) => {
    setSearchTerm(e.target.value);
  };

  const translate = useTranslate();

  const { displayGames, providers, types } = useMemo(() => {
    let allGames = [...(gamesData || [])].map((x) => {
      const id = x.provider?.id || x.providerId || otherId;
      if (!allProviders[id]) {
        allProviders[id] = {
          ...(providerMetas[x.provider?.code || otherId] || {}),
          ...(x.provider || {}),
        };
      }
      if (!x.providerId) {
        x.providerId = otherId;
      }

      const popularOffset = allProviders[id]?.popularIndex || 0;
      const popularIndex =
        x.popularIndex > 0 ? popularOffset + x.popularIndex : x.popularIndex;
      const created =
        typeof x.created === 'string' ? new Date(x.created) : x.created;

      return {
        ...x,
        created,
        popularIndex,
        isPopular: popularIndex > 0,
        type: x.type === 'CRASH' ? 'CASUAL' : x.type,
      };
    });

    allGames
      .filter((x) => x.provider?.code === 'rc')
      .sort((a, b) => b.popularIndex - a.popularIndex)
      .forEach((x, i) => {
        x.isPopular = i < 10;
      });

    const allGameTypes = {};
    allGames.forEach((x) => {
      allGameTypes[x.type] = gameTypeMetas[x.type]?.index || 10000;
    });
    const gameTypes = Object.keys(allGameTypes).sort(
      (a, b) => allGameTypes[a] - allGameTypes[b],
    );

    if (filterBy) {
      const filteredGames = allGames.filter((x) => {
        if (filterBy && !['all', 'created', 'playCount'].includes(filterBy)) {
          if (filterBy.includes('type|')) {
            return `type|${x.type}` === filterBy;
          }
          return x.providerId === filterBy;
        }
        return true;
      });
      if (filteredGames.length > 0) {
        allGames = filteredGames;
      }

      if (filterBy === 'type|CASUAL') {
        topRcCasualGames.forEach((code, i) => {
          const game = allGames.find((g) => g.code === code);
          if (game) {
            game.popularIndex = 10000 - i;
            game.isPopular = true;
          }
        });
      }
    }

    if (sortBy && sortDirection) {
      let field = sortBy;
      if (field === 'popular') {
        field = 'popularIndex';
      } else if (field === 'new') {
        field = 'created';
      }

      const sortFn = (a, b) =>
        direction === 'asc' ? a[field] - b[field] : b[field] - a[field];
      const direction = sortDirection;

      if (isPxplay && (field === 'popularIndex' || field === 'created')) {
        const predicate = (x) =>
          field === 'popularIndex' ? x.popularIndex > 0 : !!x.isNew;
        const popularIndexGames = allGames.filter(predicate).sort(sortFn);
        const otherGames = allGames.filter((x) => !predicate(x));
        const ppGames = otherGames
          .filter((x) => x.provider?.code === 'pp')
          .sort(sortFn);
        const pgGames = otherGames
          .filter((x) => x.provider?.code === 'pg')
          .sort(sortFn);
        const hsGames = otherGames
          .filter((x) => x.provider?.code === 'hs')
          .sort(sortFn);
        allGames = [...popularIndexGames, ...ppGames, ...pgGames, ...hsGames];
      } else {
        if (field === 'created') {
          allGames.forEach((x) => {
            if (x.isNew) {
              x.created = new Date();
            }
          });
        }
        allGames.sort(sortFn);
      }
    }

    const providersArr = Object.values(allProviders).sort(
      (a, b) => a.index - b.index,
    );

    return {
      displayGames: allGames,
      providers: providersArr.length > 1 ? providersArr : [],
      types: gameTypes,
    };
  }, [gamesData, sortBy, sortDirection, filterBy]);

  const menuItems = useMemo(() => {
    if (isPxplay) return [];

    const results = [
      {
        eventKey: 'all',
        label: translate('common.text.allGames', 'All'),
      },
    ];

    if (isRc) {
      const capitalize = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      };
      results.push(
        ...types.map((x) => ({
          eventKey: `type|${x}`,
          label: capitalize(x),
        })),
      );
    } else {
      results.push(
        ...providers.map((x) => ({
          eventKey: x.id,
          label: x.name,
        })),
      );
    }

    return results;
  }, [oc, providers, types, translate]);

  useEffect(() => {
    if (searchTerm !== '') {
      setGamesData(
        originalData.filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    } else {
      setGamesData(originalData);
    }
  }, [searchTerm]);

  const debouncedResults = React.useMemo(
    () => debounce(handleSearchGame, 300),
    [],
  );

  useEffect(() => () => {
    debouncedResults.cancel();
  });

  const fetchData = async (params) => {
    setLoading(false);

    const path = '/api/v1/lobby-available-games';
    const response = await fetch(`${path}?${new URLSearchParams(params)}`);
    const data = await response.json();

    if (data) {
      const games =
        data.data
          ?.filter((x) => x.status === 'ACTIVE')
          ?.map((x) => ({
            ...x,
            rtp: round(
              x.rtp
                ? (Number(x.rtp) <= 1 && Number(x.rtp * 100)) || Number(x.rtp)
                : 0,
              2,
            ),
            maxWin: round(x.maxWin ? Number(x.maxWin) : 0, 0),
          })) ?? [];
      setGamesData(games);
      setOriginalData(games);
    } else {
      setGamesData([]);
      setOriginalData([]);
    }
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (i18n?.locale) {
      fetchData({
        oc,
        t,
        l,
      });
    } else {
      setLoading(false);
    }
  }, [filterBy, i18n?.locale]);

  const handleFilter = (key) => {
    setFilterBy(key);
    updateSearchParams('filter_by', key !== 'all' && key);
  };

  return (
    <div className={`home-page__wrapper ${(loading && '--loading') || ''}`}>
      {!loading && (
        <>
          {!isPxplay && (
            <div
              className={`search-container search-container--${
                isDarkTheme ? 'dark' : 'light'
              }`}
            >
              <div className="search-icon">
                <Search />
              </div>
              <input
                type="text"
                className="form-control"
                placeholder={translate('common.text.search', 'Search')}
                onChange={debouncedResults}
              />
            </div>
          )}
          <div className="home-page__tabs-container">
            <div className="home-page__tabs-container__controls">
              <Tabs
                className="tabs"
                variant="pills"
                activeKey={filterBy}
                onSelect={(e) => handleFilter(e)}
              >
                {menuItems.map((item) => (
                  <Tab
                    key={`menu-item-${item.eventKey}`}
                    eventKey={item.eventKey}
                    className="home-page__tabs-item"
                    tabClassName={`nav-link--${isDarkTheme ? 'dark' : 'light'}`}
                    title={
                      <div className="home-page__tabs-item__title">
                        <Grid3x3GapFill
                          size={14}
                          fill={isDarkTheme ? '#ffffff' : '#000000'}
                        />
                        <Label isDarkTheme={isDarkTheme}>{item.label}</Label>
                      </div>
                    }
                  />
                ))}
              </Tabs>
              <GameSort />
            </div>
            <AllGames games={displayGames} />
          </div>
        </>
      )}
      {loading && (
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      )}
    </div>
  );
}

function Label({ isDarkTheme, children }) {
  let shouldPreventTranslate = false;
  const { replaced, content } = usePreventTranslate(children);

  if (replaced) shouldPreventTranslate = true;

  return (
    <span
      className={`tab-label tab-label--${isDarkTheme ? 'dark' : 'light'} ${
        shouldPreventTranslate ? 'notranslate' : ''
      }`}
    >
      {content}
    </span>
  );
}

Label.propTypes = {
  isDarkTheme: PropTypes.bool,
  children: PropTypes.node,
};

export default HomeContainer;
